import React, { useState, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  momentLocalizer,
  Event as CalendarEvent,
} from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { EventList } from "@/components/events/EventList";
import { useEvents } from "@/hooks/useEvents";
import { useAuth } from "@/hooks/useAuth";
import { useVehicle } from "@/hooks/useVehicles";
import { loginRedirectState } from "@/utils/auth";
import { Event } from "@/types";
import { CalendarDays, X } from "lucide-react";
import { getEventTypeColor, CALENDAR_LEGEND_TYPES } from "@/config/eventColors";
import CreateAction from "@/components/common/CreateAction";

const localizer = momentLocalizer(moment);

type ViewType = "upcoming" | "past" | "calendar";
const MY_EVENTS_PARAM = "mine";

export const EventsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(["events", "common"]);

  // Get active view from URL query params (defaults to "upcoming")
  const vehicleIdParam = searchParams.get("vehicleId");
  // When pre-filtered by vehicle, default to the past view (latest-first).
  const activeView =
    (searchParams.get("view") as ViewType) ||
    (vehicleIdParam ? "past" : "upcoming");
  const showMyEventsOnly = searchParams.get(MY_EVENTS_PARAM) === "1";
  // The API rejects vehicleId-filtered requests without auth; ignore the
  // param for signed-out visitors instead of erroring.
  const activeVehicleFilter =
    vehicleIdParam && user ? vehicleIdParam : undefined;

  const updateSearchParams = (updates: Record<string, string | null>) => {
    const nextParams = new URLSearchParams(searchParams);

    for (const [key, value] of Object.entries(updates)) {
      if (value === null) {
        nextParams.delete(key);
      } else {
        nextParams.set(key, value);
      }
    }

    setSearchParams(nextParams);
  };

  // Pagination state for list view
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Track the current calendar date (which month/week/day is being displayed)
  const [currentDate, setCurrentDate] = useState(new Date());

  // Track the current calendar date range
  const [calendarRange, setCalendarRange] = useState<{
    start: Date;
    end: Date;
  } | null>(null);

  // Convert calendar range to API parameters for date filtering
  const dateRangeParams = useMemo(() => {
    if (activeView === "calendar" && calendarRange) {
      return {
        startDateFrom: calendarRange.start.toISOString(),
        startDateTo: calendarRange.end.toISOString(),
      };
    }
    return {};
  }, [activeView, calendarRange]);

  // Pagination parameters for list view
  const paginationParams = useMemo(() => {
    if (activeView !== "calendar") {
      return {
        limit: ITEMS_PER_PAGE,
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
      };
    }
    return {};
  }, [activeView, currentPage, ITEMS_PER_PAGE]);

  // Reset pagination when switching views or changing filters
  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeView, showMyEventsOnly, activeVehicleFilter]);

  // Build query parameters based on filter state
  const queryParams = useMemo(() => {
    const params = {
      ...dateRangeParams,
      ...paginationParams,
      ...(activeVehicleFilter ? { vehicleId: activeVehicleFilter } : {}),
      ...(activeView === "upcoming"
        ? {
            upcoming: true,
            orderBy: "startDate" as const,
            orderDirection: "asc" as const,
          }
        : {}),
      ...(activeView === "past"
        ? {
            past: true,
            orderBy: "startDate" as const,
            orderDirection: "desc" as const,
          }
        : {}),
    };

    // Add userId filter if showing only user's events
    if (showMyEventsOnly && user) {
      return { ...params, userId: user.id };
    }

    return params;
  }, [
    activeView,
    showMyEventsOnly,
    activeVehicleFilter,
    user,
    dateRangeParams,
    paginationParams,
  ]);

  // Single query for events with dynamic parameters
  const { data, isLoading, error } = useEvents(queryParams);

  // Vehicle name for the active vehicle filter chip
  const { data: filterVehicle } = useVehicle(activeVehicleFilter ?? "");
  const filterVehicleName = filterVehicle
    ? filterVehicle.name ||
      `${filterVehicle.year} ${filterVehicle.maker} ${filterVehicle.model}`
    : "";

  // Get events and total count from the query
  const events = useMemo(() => data?.events || [], [data?.events]);
  const totalEvents = data?.total || 0;

  const totalPages = Math.ceil(totalEvents / ITEMS_PER_PAGE);

  // Transform events to calendar format
  const calendarEvents: CalendarEvent[] = useMemo(() => {
    return events.map((event: Event) => ({
      title: event.name,
      start: new Date(event.startDate),
      end: new Date(event.endDate),
      resource: event, // Store full event data for access on click
    }));
  }, [events]);

  const handleCreateEvent = () => {
    if (!user) {
      navigate("/login", { state: loginRedirectState(location) });
      return;
    }

    navigate("/events/new");
  };

  const handleViewEvent = (event: Event) => {
    navigate(`/events/${event.id}`, {
      state: { fromSearch: location.search },
    });
  };

  const handleSelectCalendarEvent = (calendarEvent: CalendarEvent) => {
    const event = calendarEvent.resource as Event;
    navigate(`/events/${event.id}`, {
      state: { fromSearch: location.search },
    });
  };

  const handleSelectSlot = ({ start }: { start: Date }) => {
    if (!user) {
      navigate("/login", { state: loginRedirectState(location) });
      return;
    }
    // Navigate to create event page with start date pre-filled
    navigate("/events/new", { state: { startDate: start.toISOString() } });
  };

  // Handle calendar navigation (when user clicks next/prev month, etc.)
  const handleNavigate = (newDate: Date) => {
    setCurrentDate(newDate);
  };

  // Handle calendar navigation to fetch events for the visible range
  const handleRangeChange = (range: Date[] | { start: Date; end: Date }) => {
    let start: Date;
    let end: Date;

    if (Array.isArray(range)) {
      // Week or Day view returns an array of dates
      start = range[0];
      end = range[range.length - 1];
    } else {
      // Month view returns an object with start and end
      start = range.start;
      end = range.end;
    }

    // Set the end time to end of day for inclusive filtering
    end = new Date(end);
    end.setHours(23, 59, 59, 999);

    setCalendarRange({ start, end });
  };

  // Custom event styling based on event type
  const eventStyleGetter = (event: CalendarEvent) => {
    const eventData = event.resource as Event;
    const isMyEvent = user?.id === eventData.userId;

    // Get color from centralized configuration
    const colorConfig = getEventTypeColor(eventData.eventType);

    return {
      style: {
        backgroundColor: colorConfig.hex,
        opacity: isMyEvent ? 1 : 0.7,
        border: isMyEvent ? "2px solid #1e40af" : "none",
        borderRadius: "4px",
        color: "white",
      },
    };
  };

  if (error) {
    return (
 <div className="page-container">
 <div className="bg-red-50 border border-red-200 rounded-lg p-4">
 <h3 className="text-red-800 font-medium">
            {t("events:errorLoadingEvents")}
          </h3>
 <p className="text-red-600 mt-1">
            {error instanceof Error ? error.message : t("events:unknownError")}
          </p>
        </div>
      </div>
    );
  }

  return (
 <div className="page-container">
 <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center mb-3 sm:mb-6">
 <div className="min-w-0">
 <h1 className="page-title">
            {t("events:title")}
          </h1>
 <p className="page-subtitle">
            {t("events:subtitle")}
          </p>
        </div>

 <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 flex-shrink-0">
          {user && (
        <label className="flex items-center">
              <input
                type="checkbox"
                checked={showMyEventsOnly}
                onChange={(e) =>
                  updateSearchParams({
                    [MY_EVENTS_PARAM]: e.target.checked ? "1" : null,
                  })
                }
 className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
              />
 <span className="ml-2 text-sm text-secondary-700 whitespace-nowrap">
                {t("events:showMyEventsOnly")}
              </span>
            </label>
          )}
          <CreateAction
            onClick={handleCreateEvent}
            label={t("events:createEvent")}
          />
        </div>
      </div>

      {/* View Tabs */}
 <div className="flex gap-2 mb-6 border-b border-secondary-200">
        <button
          onClick={() => updateSearchParams({ view: null })}
 className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
            activeView === "upcoming"
              ? "border-primary-600 text-primary-600 font-medium"
              : "border-transparent text-secondary-600 hover:text-secondary-900 hover:border-secondary-300"
          }`}
        >
          {t("events:views.ongoingAndFuture")}
        </button>
        <button
          onClick={() => updateSearchParams({ view: "past" })}
 className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
            activeView === "past"
              ? "border-primary-600 text-primary-600 font-medium"
              : "border-transparent text-secondary-600 hover:text-secondary-900 hover:border-secondary-300"
          }`}
        >
          {t("events:views.past")}
        </button>
        <button
          onClick={() => updateSearchParams({ view: "calendar" })}
 className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
            activeView === "calendar"
              ? "border-primary-600 text-primary-600 font-medium"
              : "border-transparent text-secondary-600 hover:text-secondary-900 hover:border-secondary-300"
          }`}
        >
 <CalendarDays className="w-4 h-4" />
          {t("events:views.calendar")}
        </button>
      </div>

      {/* Active vehicle filter chip */}
      {activeVehicleFilter && (
        <div className="mb-4 inline-flex items-center gap-2 bg-primary-50 border border-primary-200 text-primary-700 rounded-full px-3 py-1 text-sm">
          <span>{t("events:vehicleFilter", { name: filterVehicleName })}</span>
          <button
            onClick={() => updateSearchParams({ vehicleId: null })}
            className="p-0.5 rounded-full hover:bg-primary-100"
            aria-label={t("events:clearVehicleFilter")}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* List View */}
      {activeView !== "calendar" && (
        <>
          <EventList
            events={events}
            view={activeView as "upcoming" | "past"}
            isLoading={isLoading}
            onView={handleViewEvent}
            emptyMessage={t("events:noEventsMessage")}
          />

          {/* Pagination Controls */}
          {!isLoading && totalPages > 1 && (
 <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Page Info */}
              <div className="text-sm text-secondary-700">
                {t("events:pagination.showingRange", {
                  start: (currentPage - 1) * ITEMS_PER_PAGE + 1,
                  end: Math.min(currentPage * ITEMS_PER_PAGE, totalEvents),
                  total: totalEvents,
                })}
              </div>

              {/* Page Navigation */}
 <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-lg border border-secondary-300 text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t("common:previous")}
                </button>

                {/* Page Numbers */}
 <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((page) => {
                      // Show first page, last page, current page, and pages around current
                      return (
                        page === 1 ||
                        page === totalPages ||
                        Math.abs(page - currentPage) <= 1
                      );
                    })
                    .map((page, index, array) => {
                      // Add ellipsis if there's a gap
                      const previousPage = array[index - 1];
                      const showEllipsis =
                        previousPage && page - previousPage > 1;

                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && (
 <span className="px-2 py-1 text-secondary-500">...</span>
                          )}
                          <button
                            onClick={() => setCurrentPage(page)}
 className={`px-3 py-1 rounded-lg transition-colors ${
                              currentPage === page
                                ? "bg-primary-600 text-white"
                                : "border border-secondary-300 text-secondary-700 hover:bg-secondary-50"
                            }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}
                </div>

                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-lg border border-secondary-300 text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t("common:next")}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Calendar View */}
      {activeView === "calendar" && (
        <>
          {/* Calendar Legend */}
 <div className="mb-4 p-4 bg-secondary-50 rounded-lg border border-secondary-200">
 <h3 className="text-sm font-semibold text-secondary-700 mb-2">
              {t("events:calendar.legend")}
            </h3>
 <div className="flex flex-wrap gap-3">
              {CALENDAR_LEGEND_TYPES.map((eventType) => {
                const colorConfig = getEventTypeColor(eventType);
                return (
 <div key={eventType} className="flex items-center gap-2">
                    <div
 className={`w-4 h-4 ${colorConfig.tailwind} rounded`}
                    ></div>
 <span className="text-xs text-secondary-600">
                      {t(colorConfig.label)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Calendar */}
          <div
 className="bg-white rounded-lg border border-secondary-200 p-4"
            style={{ height: "700px" }}
          >
            {isLoading ? (
 <div className="flex items-center justify-center h-full">
 <div className="text-secondary-500">{t("common:loading")}</div>
              </div>
            ) : (
              <Calendar
                localizer={localizer}
                events={calendarEvents}
                startAccessor="start"
                endAccessor="end"
                date={currentDate}
                onNavigate={handleNavigate}
                onSelectEvent={handleSelectCalendarEvent}
                onSelectSlot={handleSelectSlot}
                onRangeChange={handleRangeChange}
                selectable
                eventPropGetter={eventStyleGetter}
                views={["month", "week", "day"]}
                defaultView="month"
                popup
                style={{ height: "100%" }}
              />
            )}
          </div>

          {/* Help Text */}
 <div className="mt-4 text-sm text-secondary-600">
            <p>{t("events:calendar.clickEventToView")}</p>
            <p>{t("events:calendar.clickDateToCreate")}</p>
          </div>
        </>
      )}

    </div>
  );
};
